import type {
  UserProfile,
  ProfileUpdateRequest,
  ProfileUpdateResponse,
  GenerateSignedUrlsRequest,
  GenerateSignedUrlsResponse,
  ProfilePictureUploadResponse
} from '@/features/user/types/types';
import { publicApiClient } from '@/lib/api/client';
import { parsePhoneNumberFromString, getCountryCallingCode } from 'libphonenumber-js';
import type { CountryCode } from 'libphonenumber-js';
import { getSignedMediaUrl } from '@/lib/utils';
import { userValidationService } from './userValidationService';

/**
 * Profile Service
 * 
 * Handles all profile-related operations including:
 * - Profile data management
 * - Profile picture upload using the 3-step flow:
 *   1. Generate signed URLs via POST /api/public/v1/signed-url/generate-urls
 *   2. Upload file directly to Google Cloud Storage using the signed URL
 *   3. Notify backend via PUT /api/public/v1/public-user/signed-url/:id/profile-picture
 * 
 * The uploaded images can be displayed using the getSignedMediaURL utility function.
 */
export const profileService = {
  async getProfile(): Promise<UserProfile> {
    // TODO: Implement API call to get user profile
    // For now, return empty data
    return {
      first_name: "",
      last_name: "",
      dob: "",
      phone_number: {
        country_code: "GB",
        number: ""
      },
      gender: ""
    };
  },

  async updateProfile(userData: UserProfile): Promise<ProfileUpdateResponse> {
    try {
      // Ensure date is in ISO format with timezone
      let formattedDob = userData.dob;
      if (userData.dob && !userData.dob.includes('T')) {
        // If date doesn't have time, add default time and timezone
        formattedDob = `${userData.dob}T00: 00:00Z`;
      }

      // Ensure we have a valid country code
      const countryCode = userData.phone_number.country_code || 'GB';

      // Format phone number properly
      let formattedPhoneNumber = userData.phone_number.number;
      try {
        const fullPhoneNumber = `+${getCountryCallingCode(countryCode as CountryCode)}${userData.phone_number.number}`;
        const parsed = parsePhoneNumberFromString(fullPhoneNumber);
        if (parsed && parsed.isValid()) {
          // Use the national number format (without country code)
          formattedPhoneNumber = parsed.nationalNumber;
          console.log('Formatted phone number:', {
            original: userData.phone_number.number,
            countryCode: countryCode,
            formatted: formattedPhoneNumber,
            fullNumber: fullPhoneNumber
          });
        } else {
          console.warn('Invalid phone number format:', fullPhoneNumber);
        }
      } catch (error) {
        console.warn('Could not format phone number:', error);
      }

      // Convert UserProfile to ProfileUpdateRequest format
      const updateRequest: ProfileUpdateRequest = {
        first_name: userData.first_name,
        last_name: userData.last_name,
        dob: formattedDob,
        phone_number: {
          country_code: countryCode,
          number: formattedPhoneNumber
        },
        gender: userData.gender
      };

      console.log('Sending profile update request:', updateRequest);

      // Make API call to update profile
      const response = await publicApiClient.put<ProfileUpdateResponse>(
        `/api/public/v1/public-user/update`,
        updateRequest
      );

      return response;
    } catch (error) {
      console.error('Error updating profile:', error);
      throw new Error('Failed to update profile. Please try again.');
    }
  },

  /**
   * Generates signed URLs for file upload
   * @param count - Number of URLs to generate (should be 1 for profile picture)
   * @returns Object containing UUID and signed URL mapping
   */
  async generateSignedUrls(count: number = 1): Promise<GenerateSignedUrlsResponse> {
    try {
      const request: GenerateSignedUrlsRequest = { count };
      const response = await publicApiClient.post<GenerateSignedUrlsResponse>(
        '/api/public/v1/signed-url/generate-urls',
        request
      );
      return response;
    } catch (error) {
      console.error('Error generating signed URLs:', error);
      throw new Error('Failed to generate upload URLs. Please try again.');
    }
  },

  /**
   * Uploads file directly to Google Cloud Storage using signed URL
   * @param file - The file to upload
   * @param signedUrl - The signed URL for upload
   * @returns Promise that resolves when upload is complete
   */
  async uploadProfilePictureToStorage(file: File, signedUrl: string): Promise<void> {
    try {
      // In development, use the proxy to avoid CORS issues
      const isDevelopment = import.meta.env.DEV;
      let uploadUrl = signedUrl;

      if (isDevelopment && signedUrl.includes('storage.googleapis.com')) {

        const url = new URL(signedUrl);
        let pathWithoutBucket = url.pathname;
        if (pathWithoutBucket.startsWith('/nightsyncio/')) {
          pathWithoutBucket = pathWithoutBucket.replace(/^\/nightsyncio/, '');
        }
        uploadUrl = `/gcs${pathWithoutBucket}${url.search}`;
      }

      const response = await fetch(uploadUrl, {
        method: 'PUT',
        body: file,
        headers: {
          'Content-Type': 'application/octet-stream',
        },
      });

      if (!response.ok) {
        throw new Error(`Upload failed with status: ${response.status}`);
      }
    } catch (error) {
      console.error('Error uploading file to storage:', error);
      throw new Error('Failed to upload file. Please try again.');
    }
  },

  /**
   * Notifies backend that profile picture has been uploaded
   * @param signedUrlId - The UUID from the signed URL generation
   * @returns Updated user profile with new profile picture ID
   */
  async updateProfilePicture(signedUrlId: string): Promise<ProfilePictureUploadResponse> {
    try {
      const response = await publicApiClient.put<ProfilePictureUploadResponse>(
        `/api/public/v1/public-user/signed-url/${signedUrlId}/profile-picture`
      );

      return response;
    } catch (error) {
      console.error('Error updating profile picture:', error);
      throw new Error('Failed to update profile picture. Please try again.');
    }
  },

  /**
   * Complete profile picture upload flow
   * @param file - The file to upload
   * @returns The media ID for displaying the uploaded image
   */
  async uploadProfilePicture(file: File): Promise<string | null> {
    try {
      // Step 1: Generate signed URLs
      const signedUrlsResponse = await this.generateSignedUrls(1);
      const urls = signedUrlsResponse.data.urls;

      // Get the first (and only) UUID and URL
      const [signedUrlId, signedUrl] = Object.entries(urls)[0];

      // Step 2: Upload file to Google Cloud Storage
      await this.uploadProfilePictureToStorage(file, signedUrl);

      // Step 3: Notify backend about the upload
      await this.updateProfilePicture(signedUrlId);

      // Step 4: Get updated user data to retrieve the new profile picture ID
      const updatedUser = await this.getUpdatedUserData();
      const profilePictureId = updatedUser.profile_picture_id;

      return profilePictureId;
    } catch (error) {
      console.error('Error in complete upload flow:', error);
      throw error;
    }
  },

  /**
   * Gets updated user data after profile picture upload
   * @returns Updated user data with new profile picture ID
   */
  async getUpdatedUserData(): Promise<{ profile_picture_id: string | null }> {
    try {
      const response = await userValidationService.validateUser();
      const profilePictureId = response.data.user.ProfilePictureID;

      return { profile_picture_id: profilePictureId };
    } catch (error) {
      console.error('Error fetching updated user data:', error);
      throw new Error('Failed to get updated user data. Please refresh the page.');
    }
  },

  /**
   * Gets the signed URL for a profile picture
   * @param profilePictureId - The profile picture ID
   * @returns The signed URL for displaying the image
   */
  getProfilePictureUrl(profilePictureId: string): string {
    if (!profilePictureId || profilePictureId === '00000000-0000-0000-0000-000000000000') {
      return '';
    }
    return getSignedMediaUrl(profilePictureId, 'image').url;
  }
};

